/**
 * Deploy-target helpers — resolve framework-specific install paths.
 *
 * This module contains only deploy path resolution. All catalog, repository,
 * and history I/O has moved to @skaile/asset-manager (project-local).
 */

import { lstatSync } from "node:fs";
import { homedir } from "node:os";
import { join, resolve } from "node:path";
import type { DriverTarget } from "./driver-targets.js";
import { type AiComponent, DRIVER_TARGETS } from "./driver-targets.js";

// ── Deploy-target helpers ─────────────────────────────────────────────────────

/**
 * Resolve the base deploy directory for an asset kind in a given driver target + scope.
 * For local scope, the path is relative to `cwd` (defaults to `process.cwd()`).
 * For global scope, `~` is expanded to the home directory.
 *
 * @param kind - Asset component type (e.g. `"skill"`, `"agent"`)
 * @param target - Driver target identifier (e.g. `"claude-code"`, `"omp"`)
 * @param globalInstall - `true` for user-global install, `false` for project-local
 * @param cwd - Working directory for project-local installs (default: `process.cwd()`)
 * @returns Absolute path to the base deploy directory
 * @throws When `target` is not found in `DRIVER_TARGETS`
 * @docLink packages/core/concepts#deployed-base
 */
export function deployedBase(
  kind: AiComponent,
  target: DriverTarget,
  globalInstall: boolean,
  cwd?: string,
): string {
  const targetMap = DRIVER_TARGETS[target];
  if (!targetMap) throw new Error(`Unknown deploy target: '${target}'`);
  const scope = globalInstall ? "global" : "local";
  const paths = targetMap[scope];
  const destStr = paths[kind] ?? paths.skill ?? "";
  if (globalInstall) {
    return destStr.replace(/^~/, homedir());
  }
  return resolve(cwd ?? process.cwd(), destStr);
}

/**
 * Return the full deploy path for a named asset: `<deployedBase>/<name>`.
 *
 * @param kind - Asset component type
 * @param name - Asset name (becomes the directory/file name under the base)
 * @param target - Driver target identifier
 * @param globalInstall - `true` for user-global, `false` for project-local
 * @param cwd - Working directory for local installs
 * @returns Absolute path to the asset's deploy directory
 * @docLink packages/core/concepts#deployed-dir
 */
export function deployedDir(
  kind: AiComponent,
  name: string,
  target: DriverTarget,
  globalInstall: boolean,
  cwd?: string,
): string {
  return join(deployedBase(kind, target, globalInstall, cwd), name);
}

/**
 * Check whether an asset is already deployed at its expected location.
 * Uses `lstatSync` so symlinks are treated as deployed.
 *
 * @param kind - Asset component type
 * @param name - Asset name
 * @param target - Driver target identifier
 * @param globalInstall - `true` for user-global, `false` for project-local
 * @param cwd - Working directory for local installs
 * @returns `true` if the deploy path exists, `false` otherwise
 * @docLink packages/core/concepts#is-deployed
 */
export function isDeployed(
  kind: AiComponent,
  name: string,
  target: DriverTarget,
  globalInstall: boolean,
  cwd?: string,
): boolean {
  try {
    lstatSync(deployedDir(kind, name, target, globalInstall, cwd));
    return true;
  } catch {
    return false;
  }
}
